// Decode a JPEG only as large as needed. JPEG can be decoded at 1/2, 1/4 or 1/8 scale almost for free
// (DCT scaling); WebCodecs' ImageDecoder does that when asked for a smaller size, while
// createImageBitmap always decodes in full first (see src/lib/scan/scaledDecode.js, which uses the same
// trick for the detector input). Used by the preview copy (1200px out of a 24MP original only needs a
// 1/4 decode) and by crop-back (crops capped at 450 / 1500px often need half the pixels or fewer).
//
// Every step falls back: no ImageDecoder, not a JPEG, a progressive JPEG (no cheap scaling), a mirrored
// EXIF orientation, or a decoder that reports anything unexpected → null, and the caller does the
// ordinary full decode (decodeFull, which itself tries every route a browser may offer). The returned
// bitmap is upright (EXIF orientation applied).
import { parseJpegInfo } from "@/lib/scan/scaledDecode";
import { swapsAxes, type Drawable } from "./exifOrientation";

const HEADER_BYTES = 512 * 1024; // EXIF (incl. maker notes + thumbnail) precedes the frame header
// Rotation (degrees clockwise) a decoder reports for each non-mirrored EXIF orientation.
const ROTATION: Record<number, number> = { 1: 0, 3: 180, 6: 90, 8: 270 };

export interface JpegHeader {
  /** Upright (EXIF-oriented) size. */
  width: number;
  height: number;
  orientation: number;
  progressive: boolean;
}

const bytesOf = (blob: Blob): Promise<ArrayBuffer> =>
  typeof blob.arrayBuffer === "function"
    ? blob.arrayBuffer()
    : new Promise((resolve, reject) => {
        // Older engines / jsdom: FileReader.
        const r = new FileReader();
        r.onload = () => resolve(r.result as ArrayBuffer);
        r.onerror = () => reject(r.error);
        r.readAsArrayBuffer(blob);
      });

/** Upright size, orientation and progressive flag of a JPEG from its first bytes; null if not a JPEG. */
export async function readJpegHeader(file: Blob): Promise<JpegHeader | null> {
  try {
    const info = parseJpegInfo(new Uint8Array(await bytesOf(file.slice(0, HEADER_BYTES)))) as
      | { width: number; height: number; orientation: number; progressive: boolean }
      | null;
    if (!info) return null;
    const swap = info.orientation >= 5;
    return {
      width: swap ? info.height : info.width,
      height: swap ? info.width : info.height,
      orientation: info.orientation,
      progressive: info.progressive,
    };
  } catch {
    return null;
  }
}

/** The largest DCT scale (8, 4, 2) that still keeps `fraction` (0..1] of the full resolution, else 1. */
export function pickScale(fraction: number, header: JpegHeader | null): number {
  if (!header || header.progressive || !(header.orientation in ROTATION) || !(fraction > 0)) return 1;
  for (const s of [8, 4, 2]) if (1 / s >= fraction) return s;
  return 1;
}

type Decoded = { image: { codedWidth: number; codedHeight: number; rotation?: number; flip?: boolean; close(): void } };
type Decoder = { decode(): Promise<Decoded>; close(): void };
type DecoderCtor = new (init: { data: ReadableStream; type: string; desiredWidth: number; desiredHeight: number }) => Decoder;

/** ImageDecoder decode at 1/scale (1 = full size), upright, or null on anything unexpected. */
async function decodeViaImageDecoder(file: Blob, header: JpegHeader, scale: number): Promise<ImageBitmap | null> {
  const Ctor = (globalThis as unknown as { ImageDecoder?: DecoderCtor }).ImageDecoder;
  if (!Ctor || typeof createImageBitmap === "undefined") return null;
  const rotation = ROTATION[header.orientation];
  if (rotation === undefined) return null;
  const swap = rotation === 90 || rotation === 270;
  // The decoder works on the STORED (pre-rotation) frame.
  const rawW = swap ? header.height : header.width;
  const rawH = swap ? header.width : header.height;
  const desiredWidth = Math.ceil(rawW / scale);
  const desiredHeight = Math.ceil(rawH / scale);
  let decoder: Decoder | null = null;
  let frame: Decoded["image"] | null = null;
  try {
    decoder = new Ctor({ data: file.stream(), type: "image/jpeg", desiredWidth, desiredHeight });
    frame = (await decoder.decode()).image;
    // createImageBitmap(frame) applies the rotation the decoder reports — it must be the EXIF one.
    if ((typeof frame.rotation === "number" ? frame.rotation : 0) !== rotation || frame.flip === true) return null;
    if (frame.codedWidth !== desiredWidth || frame.codedHeight !== desiredHeight) return null;
    const bmp = await createImageBitmap(frame as unknown as ImageBitmapSource);
    if (bmp.width !== (swap ? desiredHeight : desiredWidth) || bmp.height !== (swap ? desiredWidth : desiredHeight)) {
      bmp.close();
      return null;
    }
    return bmp;
  } catch {
    return null;
  } finally {
    try { frame?.close(); } catch { /* already closed */ }
    try { decoder?.close(); } catch { /* already closed */ }
  }
}

/** Upright bitmap decoded at 1/scale, or null (the caller then decodes in full). */
export async function decodeScaled(file: Blob, header: JpegHeader | null, scale: number): Promise<ImageBitmap | null> {
  // Progressive frames can't be DCT-scaled cheaply: a "reduced" decode would cost about a full one.
  if (scale <= 1 || !header || header.progressive) return null;
  return decodeViaImageDecoder(file, header, scale);
}

/**
 * The FULL-size upright decode, by whichever route this browser supports, or null when none works.
 *
 * createImageBitmap(file, {imageOrientation: 'from-image'}) is the usual one, but some browsers reject it
 * (the option value, or a full-size decode in a worker) while their ImageDecoder decodes the very same file
 * — the reduced decodes above prove it works — so ImageDecoder at full size comes next, then a plain
 * createImageBitmap for an unrotated photo. With a header, a result must have its upright size (±1px): a
 * decode that ignored the EXIF orientation would put every crop in the wrong place. Without one, the first
 * route's bitmap is returned as-is (the old behaviour).
 */
export async function decodeFull(file: Blob, header: JpegHeader | null): Promise<ImageBitmap | null> {
  if (typeof createImageBitmap === "undefined") return null;
  const upright = (bmp: ImageBitmap | null): ImageBitmap | null => {
    if (!bmp || !header) return bmp;
    if (Math.abs(bmp.width - header.width) <= 1 && Math.abs(bmp.height - header.height) <= 1) return bmp;
    try { bmp.close(); } catch { /* ignore */ }
    return null;
  };
  const routes: Array<() => Promise<ImageBitmap | null>> = [
    () => createImageBitmap(file, { imageOrientation: "from-image" }),
    async () => (header ? decodeViaImageDecoder(file, header, 1) : null),
    async () => (header && header.orientation === 1 ? createImageBitmap(file) : null),
  ];
  for (const route of routes) {
    try {
      const bmp = upright(await route());
      if (bmp) return bmp;
    } catch {
      // this route isn't available here — try the next
    }
  }
  return null;
}

const near = (w: number, h: number, W: number, H: number) => Math.abs(w - W) <= 1 && Math.abs(h - H) <= 1;

const bitmapDrawable = (bmp: ImageBitmap, orientation: number): Drawable => ({
  source: bmp,
  width: bmp.width,
  height: bmp.height,
  orientation,
  close: () => { try { bmp.close(); } catch { /* already closed */ } },
});

/**
 * The browser's DEFAULT decode, createImageBitmap(file) with no options, for a rotated photo. The spec's
 * default orientation is 'from-image', so this is upright wherever the explicit option is missing: Safari
 * before 17 rejects imageOrientation:'from-image' ("TypeError: Type error") yet applies the EXIF tag by
 * default (seen on WebKit 15.4 and 16.0), which left every portrait photo with no decode route in Safari.
 * The size must prove it: upright → used as is; for a 90° orientation, the stored size → the orientation is
 * applied while drawing (exifOrientation.drawUpright), for an engine that ignores the tag. For 180° and
 * mirrored photos the two sizes are the same, and the spec's default (applied) is taken.
 */
async function decodeDefault(file: Blob, header: JpegHeader): Promise<Drawable | null> {
  if (typeof createImageBitmap === "undefined") return null;
  try {
    const bmp = await createImageBitmap(file);
    if (near(bmp.width, bmp.height, header.width, header.height)) return bitmapDrawable(bmp, 1);
    if (swapsAxes(header.orientation) && near(bmp.width, bmp.height, header.height, header.width)) return bitmapDrawable(bmp, header.orientation);
    bmp.close();
  } catch {
    // no decode here
  }
  return null;
}

/**
 * Any usable decode of a JPEG, as a Drawable (exifOrientation.ts), by the routes a worker has: the upright
 * ones first (a DCT-scaled ImageDecoder decode at 1/scale, then decodeFull), then the browser's default
 * decode (decodeDefault — Safari: no ImageDecoder, no 'from-image' option before 17). Null when nothing
 * decodes it. Its upright size is the header's (±1px), or 1/scale of it.
 */
export async function decodeDrawable(file: Blob, header: JpegHeader | null, scale = 1): Promise<Drawable | null> {
  try {
    const bmp = await decodeScaled(file, header, scale);
    if (bmp) return bitmapDrawable(bmp, 1);
  } catch { /* next route */ }
  try {
    const bmp = await decodeFull(file, header);
    if (bmp) return bitmapDrawable(bmp, 1);
  } catch { /* next route */ }
  return header ? decodeDefault(file, header) : null;
}
